import { useEffect, useRef, useState } from "react";

/**
 * Signature box. Tapping it opens the whole screen to sign on (much easier with a finger on a phone);
 * Done brings the signature back here. Calls onChange(dataUrl | "") once the kid finishes or clears.
 */
export default function SignaturePad({ onChange, height = 160 }) {
  const [open, setOpen] = useState(false);
  const [signature, setSignature] = useState("");

  const finish = (dataUrl) => {
    setOpen(false);
    if (dataUrl) {
      setSignature(dataUrl);
      onChange?.(dataUrl);
    }
  };
  const clear = () => {
    setSignature("");
    onChange?.("");
  };

  return (
    <div>
      <button type="button" onClick={() => setOpen(true)} data-testid="signature-pad"
              className="relative flex w-full items-center justify-center rounded-xl border-2 border-dashed border-[#E4E2DC] bg-white"
              style={{ height }}>
        {signature ? (
          <img src={signature} alt="Your signature" className="h-full w-full object-contain p-2" />
        ) : (
          <span className="text-sm font-medium text-[#707A6E]">✍️ Tap to sign</span>
        )}
      </button>
      {signature && (
        <div className="mt-1 flex gap-4 text-xs text-[#707A6E]">
          <button type="button" onClick={() => setOpen(true)} className="underline">Sign again</button>
          <button type="button" onClick={clear} className="underline">Clear</button>
        </div>
      )}
      {open && <FullScreenPad onDone={finish} onCancel={() => setOpen(false)} />}
    </div>
  );
}

/** The whole screen to sign on, with Clear, Cancel and Done. Saves only what's inside the strokes' bounds. */
function FullScreenPad({ onDone, onCancel }) {
  const canvas = useRef(null);
  const drawing = useRef(false);
  const bounds = useRef(null);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = canvas.current;
    const ratio = window.devicePixelRatio || 1;
    const setup = () => {
      c.width = c.offsetWidth * ratio;
      c.height = c.offsetHeight * ratio;
      const ctx = c.getContext("2d");
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.lineWidth = 3.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#2C342A";
      bounds.current = null;
      setEmpty(true);
    };
    setup();
    // Turning the phone changes the width: start again rather than show a squashed signature. Height alone changes
    // when the browser's address bar slides, which mustn't wipe what the kid drew.
    let width = c.offsetWidth;
    const onResize = () => { if (Math.abs(c.offsetWidth - width) > 40) { width = c.offsetWidth; setup(); } };
    window.addEventListener("resize", onResize);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("resize", onResize);
      document.body.style.overflow = prev;
    };
  }, []);

  const point = (e) => {
    const r = canvas.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const grow = ({ x, y }) => {
    const b = bounds.current;
    bounds.current = b ? { x0: Math.min(b.x0, x), y0: Math.min(b.y0, y), x1: Math.max(b.x1, x), y1: Math.max(b.y1, y) }
      : { x0: x, y0: y, x1: x, y1: y };
  };
  const down = (e) => {
    e.preventDefault();
    canvas.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    const p = point(e);
    grow(p);
    const ctx = canvas.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(p.x + 0.1, p.y + 0.1);
    ctx.stroke();
    setEmpty(false);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = point(e);
    grow(p);
    const ctx = canvas.current.getContext("2d");
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };
  const up = () => { drawing.current = false; };
  const clear = () => {
    const c = canvas.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    bounds.current = null;
    setEmpty(true);
  };
  const done = () => {
    const b = bounds.current;
    if (!b) return onCancel();
    // Crop to the signature (plus a margin) so it fits the small box nicely.
    const c = canvas.current;
    const ratio = c.width / c.offsetWidth;
    const pad = 12;
    const x = Math.max(0, b.x0 - pad), y = Math.max(0, b.y0 - pad);
    const w = Math.min(c.offsetWidth, b.x1 + pad) - x, h = Math.min(c.offsetHeight, b.y1 + pad) - y;
    const out = document.createElement("canvas");
    out.width = Math.round(w * ratio);
    out.height = Math.round(h * ratio);
    out.getContext("2d").drawImage(c, x * ratio, y * ratio, w * ratio, h * ratio, 0, 0, out.width, out.height);
    onDone(out.toDataURL("image/png"));
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#F7F5F0]" data-testid="signature-fullscreen">
      <div className="flex items-center justify-between gap-2 px-4 py-2">
        <button type="button" onClick={empty ? onCancel : clear}
                className="min-w-[64px] py-2 text-left text-sm font-semibold text-[#C06E52]">
          {empty ? "Cancel" : "Clear"}
        </button>
        <span className="truncate text-sm font-semibold text-[#707A6E]">Sign with your finger</span>
        <button type="button" onClick={done} disabled={empty} data-testid="signature-done"
                className="min-w-[64px] py-2 text-right text-sm font-bold text-[#5C7A4F] disabled:opacity-40">
          Done
        </button>
      </div>
      <div className="relative mx-2 mb-2 flex-1 overflow-hidden rounded-2xl bg-white">
        <canvas ref={canvas} style={{ width: "100%", height: "100%", touchAction: "none", display: "block" }}
                onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
        <div className="pointer-events-none absolute inset-x-8 bottom-[28%] h-[2px] bg-[#2C342A]/15" />
        {empty && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-[#A6ABA3]">
            Turn your phone sideways for more room
          </span>
        )}
      </div>
    </div>
  );
}
